import { queue } from "async";
import moment from "moment";
import compact from "lodash/compact";

import { BuildStates } from "~/constants/BuildStates";
import { getJson, request } from "~/lib/http";

import cable from "~/lib/cable";
import Database from "~/lib/Database";
import Logger from "~/lib/Logger";
import BaseStore from "~/stores/BaseStore";
import UserStore from "~/stores/UserStore";

export type Project = {
  id: string;
  path: string;
  defaultBranch: string | null | undefined;
  name: string;
  permissions: {
    edit: {
      allowed: boolean;
    };
    favoriteProject: {
      allowed: boolean;
    };
  };
};

export type Branch = {
  name: string;
  path: string;
  starred?: boolean;
  project?: Project;
  builds?: Array<Build>;
};

export type MergeQueue = {
  baseBranch: string;
  path: string;
  builds?: Array<Build>;
};

export type Build = {
  id: string;
  number: number;
  message: string;
  state: BuildStates;
  path: string;
  project: Project;
  commitId: string;
  commitShortLength: number;
  branchName: string;
  branchPath: string;
  createdAt: string;
  authorName: string;
  mergeQueueBaseBranch: string | null;
  mergeQueuePath: string;
};

// NOTE: Keep in sync with Project::Summary::BUILDS_PER_BRANCH_LIMIT
const BUILDS_PER_BRANCH_LIMIT = 5;

export default class BranchStore extends BaseStore {
  project: Project;
  database: Database;
  queue: { push: (data: unknown, callback?: (error?: Error) => void) => void };
  pipelineSubscription: { unsubscribe: () => void } | null;
  buildSubscriptions: { [key: string]: { unsubscribe: () => void } };
  branches: {
    [key: string]: Branch;
  };
  mergeQueues: {
    [key: string]: MergeQueue;
  };

  constructor(project: Project, builds: Array<Build>) {
    // Call super on the parent store
    super();

    // Parse the project data
    this.project = Database.parse(project);

    // Create a database and seed it with data
    this.database = new Database(builds);

    // Index the builds based on branch
    this.branches = {};
    this.mergeQueues = {};
    for (const build of this.database.all()) {
      this._parseBranchInformation(build);
    }

    // Create a queue for updates so we're not overloading the backend with requests
    this.queue = queue((number, callback) => {
      Logger.info(`[BranchStore] Refreshing build number ${number}`);

      return getJson<Build>(`${this.project.path}/builds/${number}.json?index=true`)
        .then((data) => this.loadAndEmit(data))
        .then(callback)
        .catch((error) => callback(error));
    });

    this.pipelineSubscription = cable.subscriptions.create(
      { channel: "Pipelines::PipelineChannel", uuid: this.project.id },
      {
        store: this,
        received({ event, ...payload }) {
          if (event === "build:created") {
            this.store.queue.push(payload.number);
          }
        },
      },
    );

    this.buildSubscriptions = {};
    for (const build of this.database.all()) {
      this._subscribeToBuild(build);
    }
  }

  all(): Array<Branch> {
    // The starred branches are in the order at which they should appear on the page
    let viewRecord;
    const starredBranches: Array<Branch> = [];
    this._starredBranches().forEach((branch) => {
      viewRecord = this._createBranchViewRecord(branch, true);
      if (viewRecord) {
        starredBranches.push(viewRecord);
      }
    });

    // The rest of the branches
    const nonStarredBranches: Array<Branch> = [];
    this._nonStarredBranches().forEach((branch) => {
      viewRecord = this._createBranchViewRecord(branch, false);
      if (viewRecord) {
        nonStarredBranches.push(viewRecord);
      }
    });

    // Return the view records
    return this._sortBranches(starredBranches).concat(this._sortBranches(nonStarredBranches));
  }

  getProject() {
    return this.project;
  }

  getMergeQueues() {
    const mergeQueueBaseBranches = Object.keys(this.mergeQueues);

    if (mergeQueueBaseBranches.length === 0) {
      return [];
    }

    return compact(
      mergeQueueBaseBranches.map((baseBranch) => this._createMergeQueueViewRecord(baseBranch)),
    );
  }

  removeBranch(branch: string) {
    const branchData = this.branches[branch];
    if (!branchData) {
      Logger.info(`[BranchStore] Attempted to remove non-existent branch: ${branch}`);
      return;
    }

    delete this.branches[branch];
    this.emit("change");

    request(`${this.project.path}/branches`, {
      method: "DELETE",
      json: { branch },
    }).catch((error) => {
      Logger.error(`[BranchStore] Failed to remove branch "${branch}".`, error);
    });
  }

  _sortBranches(branches: Array<Branch>): Array<Branch> {
    // First, split the branches up into default/non-default
    const defaultBranch: Array<Branch> = [];
    const otherBranches: Array<Branch> = [];

    for (const branch of branches) {
      if (this.project.defaultBranch && branch.name === this.project.defaultBranch) {
        defaultBranch.push(branch);
      } else {
        otherBranches.push(branch);
      }
    }

    // Now sort the other branches by last build
    const sortedBranches = otherBranches.sort((branchA, branchB) => {
      if (!Array.isArray(branchA.builds) || branchA.builds.length === 0) {
        return -1;
      }

      const buildALastBuild = moment(branchA.builds[0].createdAt).unix();

      if (!Array.isArray(branchB.builds) || branchB.builds.length === 0) {
        return 1;
      }

      const buildBLastBuild = moment(branchB.builds[0].createdAt).unix();

      if (buildALastBuild === buildBLastBuild) {
        return 0;
      } else if (buildALastBuild >= buildBLastBuild) {
        return -1;
      }

      return 1;
    });

    // Return the default branch first, followed by other branches
    return defaultBranch.concat(sortedBranches);
  }

  _createBranchViewRecord(branchName: string, starred: boolean) {
    const branch = this.branches[branchName];

    if (!branch) {
      return null;
    }

    return {
      name: branchName,
      starred,
      path: branch.path,
      builds: this._findBuildsForBranch(branchName).slice(0, BUILDS_PER_BRANCH_LIMIT), // only show the last BUILDS_PER_BRANCH_LIMIT
    };
  }

  _createMergeQueueViewRecord(mergeQueueBaseBranch: string) {
    const mergeQueue = this.mergeQueues[mergeQueueBaseBranch];

    if (!mergeQueue) {
      return null;
    }

    return {
      baseBranch: mergeQueue.baseBranch,
      path: mergeQueue.path,
      builds: this.database
        .all()
        .filter((build) => build.mergeQueueBaseBranch === mergeQueueBaseBranch)
        .sort((buildA, buildB) => moment(buildB.createdAt).unix() - moment(buildA.createdAt).unix())
        .slice(0, BUILDS_PER_BRANCH_LIMIT),
    };
  }

  // Find the builds for a given branch
  _findBuildsForBranch(branchName: string) {
    return (
      this.database
        .all()
        .filter((build) => build.branchName === branchName && build.mergeQueueBaseBranch === null)
        // Sort the builds on the branch by createdAt desc (newest first)
        .sort((buildA, buildB) => moment(buildB.createdAt).unix() - moment(buildA.createdAt).unix())
    );
  }

  // Stores information about the branch as we parse builds
  _parseBranchInformation(build: Build) {
    if (build.mergeQueueBaseBranch) {
      // If this build is part of a merge queue, we need to store it in the mergeQueues rather than
      // as a branch
      this.mergeQueues[build.mergeQueueBaseBranch] ??= {
        baseBranch: build.mergeQueueBaseBranch,
        path: build.mergeQueuePath,
      };
    } else {
      this.branches[build.branchName] ??= {
        name: build.branchName,
        path: build.branchPath,
      };
    }
  }

  loadAndEmit(build: Build) {
    // We only care about this build if it matches the project
    if (build.project.id === this.project.id) {
      const newBuild = this.database.load(build);
      this._parseBranchInformation(newBuild);
      this._subscribeToBuild(newBuild);
      this._cleanUpSubscriptions(newBuild.branchName);
      this.emit("change");
    }
  }

  _cleanUpSubscriptions(branchName: string) {
    this._findBuildsForBranch(branchName)
      .slice(BUILDS_PER_BRANCH_LIMIT)
      .forEach((build) => {
        if (this.buildSubscriptions[build.id]) {
          this.buildSubscriptions[build.id].unsubscribe();
          delete this.buildSubscriptions[build.id];
        }
      });
  }

  _subscribeToBuild(build: Build) {
    if (!this.buildSubscriptions[build.id]) {
      this.buildSubscriptions[build.id] = cable.subscriptions.create(
        { channel: "Pipelines::BuildChannel", uuid: build.id },
        {
          store: this,
          number: build.number,

          received({ event }) {
            // This view only cares about commit/branch/message and state
            // changes. Step and annotation changes are irrelevant.
            //
            // XXX: how do we manage a good list of state change events? Maybe
            // it should be one event with a "new state" in the payload?
            //
            if (
              event === "updated" ||
              event === "started" ||
              event === "finished" ||
              event === "skipped" ||
              event === "canceling"
            ) {
              this.store.queue.push(this.number);
            }
          },
        },
      );
    }
  }

  _starredBranches() {
    return UserStore.getStarredBranches(this.project);
  }

  // Return non-starred branches that we know about.
  _nonStarredBranches(): Array<string> {
    const starredBranches = this._starredBranches();

    return Object.keys(this.branches).filter((branch) => starredBranches.indexOf(branch) === -1);
  }
}
